import { Checkbox, CircularProgress } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import classNames from "classnames";
import * as React from "react";

import { ErrorBoundary } from "@/shared/components/ErrorBoundary";

import { type EnumComparator, isValueless } from "../FilterSchema";

import styles from "./EnumValueInput.module.css";

interface EnumValueInputProps {
  comparator: EnumComparator;
  value: string[];
  onChange: (next: string[]) => void;
  options: string[] | (() => Promise<string[]>);
}

/**
 * Value input for enum-typed filters.
 *
 * Operator-aware: CONTAINS ("is") and NOT_CONTAINS ("is not") render a
 * searchable checkbox list.
 *
 * `options` accepts either a `string[]` or a `() => Promise<string[]>`.
 * The async path runs through `useSuspenseQuery` purely for the
 * loading/error/data ergonomics — the query key is scoped to this
 * component instance (`React.useId`), so nothing is shared across
 * consumers. Caching is the consumer's responsibility (e.g. wrap the
 * function with `queryClient.ensureQueryData(...)`).
 *
 * Per the spec, dynamic enum lists are not paginated; consumers must
 * keep the option set small enough to fit in memory. Search filters
 * client-side only.
 */
export function EnumValueInput({
  comparator,
  value,
  onChange,
  options,
}: EnumValueInputProps) {
  const [searchValue, setSearchValue] = React.useState("");
  const instanceId = React.useId();

  if (isValueless(comparator)) {
    return null;
  }

  // TODO Use an MUI text input for search.
  // Or a tags autocomplete instead.
  return (
    <div className={styles.input}>
      <input
        type="search"
        className={styles.searchInput}
        placeholder="Search options..."
        aria-label="Search options"
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
      />
      <ErrorBoundary fallback={renderErrorState}>
        <React.Suspense fallback={<LoadingState />}>
          <OptionList
            instanceId={instanceId}
            options={options}
            searchValue={searchValue}
            value={value}
            onChange={onChange}
          />
        </React.Suspense>
      </ErrorBoundary>
    </div>
  );
}

interface OptionListProps {
  instanceId: string;
  options: string[] | (() => Promise<string[]>);
  searchValue: string;
  value: string[];
  onChange: (next: string[]) => void;
}

function OptionList({
  instanceId,
  options,
  searchValue,
  value,
  onChange,
}: OptionListProps) {
  const isLazy = typeof options === "function";

  // The static path supplies `initialData`, so Suspense never activates
  // for it. The lazy path has no initial data and suspends until the
  // promise resolves. Either way, `data` is `string[]` in the body.
  const { data } = useSuspenseQuery({
    queryKey: [instanceId],
    queryFn: isLazy ? options : () => Promise.resolve(options),
    initialData: isLazy ? undefined : options,
  });

  const filteredOptions = React.useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (needle === "") {
      return data;
    }
    return data.filter((option) => option.toLowerCase().includes(needle));
  }, [data, searchValue]);

  if (filteredOptions.length === 0) {
    return (
      <div className={styles.status} role="status" aria-live="polite">
        No matching options
      </div>
    );
  }

  const toggleOption = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  return (
    <ul
      className={styles.list}
      role="listbox"
      aria-label="Filterable values"
      aria-multiselectable="true"
    >
      {filteredOptions.map((option) => {
        const isSelected = value.includes(option);
        return (
          <li key={option} role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              className={styles.row}
              onClick={() => toggleOption(option)}
            >
              <Checkbox
                size="small"
                checked={isSelected}
                tabIndex={-1}
                disableRipple
                className={styles.checkbox}
              />
              <span className={styles.rowLabel}>{option}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function LoadingState() {
  return (
    <div className={styles.status} role="status" aria-live="polite">
      <CircularProgress size="1rem" />
      <span>Loading options…</span>
    </div>
  );
}

function renderErrorState() {
  return (
    <div className={classNames(styles.status, styles.error)} role="alert">
      Couldn’t load options. Reopen the menu to retry.
    </div>
  );
}
